import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Shield, ShieldAlert, ShieldCheck, AlertTriangle, Activity } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";

import { RedactedEmail } from "./RedactedEmail";

interface SecurityEvent {
  id: string;
  user_id: string;
  user_email?: string;
  event_type: string;
  severity: 'info' | 'warning' | 'critical';
  description: string;
  created_at: string;
  metadata?: any;
}

const redactEmail = (email: string): string => {
  if (!email || !email.includes("@")) return email;
  
  const [username, domain] = email.split("@");
  if (username.length <= 2) {
    return `${"*".repeat(username.length)}@${domain}`;
  }
  
  const visiblePart = username.slice(-2);
  const hiddenPart = "*".repeat(username.length - 2);
  return `${hiddenPart}${visiblePart}@${domain}`;
};

const getEventIcon = (eventType: string, severity: string) => {
  if (severity === 'critical') return <ShieldAlert className="h-4 w-4 text-destructive" />;
  if (eventType.includes('2fa') || eventType.includes('totp')) return <ShieldCheck className="h-4 w-4 text-blue-500" />;
  return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
};

const getEventLabel = (eventType: string) => {
  return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getSeverityVariant = (severity: string): "default" | "destructive" | "secondary" | "outline" => {
  switch (severity) {
    case "critical":
      return "destructive";
    case "warning":
      return "secondary";
    case "info":
      return "outline";
    default:
      return "default";
  }
};

export const SecurityAlerts = () => {
  const [alerts, setAlerts] = useState<SecurityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSecurityAlerts = async () => {
    try {
      setIsLoading(true);
      
      // 1. Fetch 2FA events (failures and critical actions)
      const { data: twoFaData, error: twoFaError } = await supabase
        .from("twofa_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (twoFaError) throw twoFaError;

      // 2. Fetch Security Violations (impossible travel, rate limits, etc.)
      const { data: violationData, error: violationError } = await supabase
        .from("security_violation_history")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (violationError) throw violationError;

      // 3. Fetch user emails for context
      const userIds = new Set([
        ...(twoFaData?.map(d => d.user_id) || []),
        ...(violationData?.map(d => d.user_id).filter(Boolean) || [])
      ]);

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", Array.from(userIds));

      const emailMap = new Map(profiles?.map(p => [p.id, p.email]));

      // 4. Transform and merge events
      const events: SecurityEvent[] = [];

      // Process 2FA events
      twoFaData?.forEach(log => {
        // Filter for interesting events (failures, security alerts, critical actions)
        if (!log.success || log.action_type === 'security_alert' || log.action_type === 'disable' || log.action_type === 'setup_complete') {
          events.push({
            id: log.id,
            user_id: log.user_id,
            user_email: emailMap.get(log.user_id) || 'Unknown',
            event_type: log.action_type,
            severity: !log.success ? 'warning' : log.action_type === 'security_alert' ? 'critical' : 'info',
            description: log.error_message || `2FA ${log.action_type}`,
            created_at: log.created_at,
            metadata: log.metadata
          });
        }
      });

      // Process security violations
      violationData?.forEach(v => {
        events.push({
          id: v.id,
          user_id: v.user_id || 'unknown',
          user_email: v.user_id ? emailMap.get(v.user_id) : 'Unknown',
          event_type: v.violation_type,
          severity: v.severity === 'critical' || v.severity === 'high' ? 'critical' : 'warning',
          description: `${v.violation_type.replace(/_/g, ' ')} detected${v.ip_address ? ` from ${v.ip_address}` : ''}`,
          created_at: v.created_at,
          metadata: v.metadata
        });
      });

      // Sort by date desc
      events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setAlerts(events);
    } catch (error) {
      console.error("Error fetching security alerts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSecurityAlerts();
    
    // Set up real-time subscriptions
    const channel = supabase
      .channel("security-events")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "twofa_audit_log",
        },
        () => {
          fetchSecurityAlerts();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "security_violation_history",
        },
        () => {
          fetchSecurityAlerts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Loading security events...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Security Events
        </CardTitle>
        <CardDescription>
          Recent security violations and 2FA alerts
        </CardDescription>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>
              No recent security alerts. All systems normal.
            </AlertDescription>
          </Alert>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <div className="mt-0.5">{getEventIcon(alert.event_type, alert.severity)}</div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge variant={getSeverityVariant(alert.severity)}>
                        {getEventLabel(alert.event_type)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(alert.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">User:</span>
                      <RedactedEmail userId={alert.user_id} redactedEmail={redactEmail(alert.user_email || '')} />
                    </div>
                    <p className="text-xs text-muted-foreground">{alert.description}</p>
                    {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer hover:text-foreground">Details</summary>
                        <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-auto">
                          {JSON.stringify(alert.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
